import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { listApiKeys } from '../apikeys/api';
import { listWallets } from '../wallets/api';

export function DashboardPage() {
  const { org } = useCurrentOrg();

  const apiKeys = useQuery({
    queryKey: ['api-keys', org?.id],
    queryFn: () => listApiKeys(org!.id),
    enabled: !!org,
  });
  const wallets = useQuery({
    queryKey: ['wallets', org?.id, { limit: 1, offset: 0 }],
    queryFn: () => listWallets(org!.id, { limit: 1, offset: 0 }),
    enabled: !!org,
  });

  if (!org) return null;

  const mode = org.auth_config.mode ?? (org.auth_config.issuer ? 'oidc' : null);
  const authConfigured =
    mode === 'sigil' || (mode === 'oidc' && !!org.auth_config.issuer && !!org.auth_config.audience);
  const hasActivePublishable =
    (apiKeys.data ?? []).some((k) => k.type === 'publishable' && !k.revoked_at);
  const hasFirstWallet = (wallets.data?.total ?? 0) > 0;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Welcome to Sigil</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Get your company ready to issue self-custodial wallets.
        </p>
      </div>

      <Card>
        <CardHeader title="Company" />
        <CardBody>
          <div className="grid gap-6 sm:grid-cols-3">
            <Field label="Name" value={org.name} />
            <Field label="Slug" value={org.slug} mono />
            <Field label="Company ID" value={org.id} mono small />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Get started" description="Three steps to start issuing wallets to your users." />
        <CardBody className="!p-0">
          <ol className="divide-y divide-line-soft">
            <Step
              n={1}
              done={authConfigured}
              title="Configure authentication"
              body="Choose Sigil-hosted email OTP or plug in your own OIDC provider."
              cta={{ to: '/authentication', label: authConfigured ? 'Review configuration' : 'Configure auth' }}
            />
            <Step
              n={2}
              done={hasActivePublishable}
              title="Create an API key"
              body="Generate a publishable key for the SDK and a secret key for server-to-server calls."
              cta={{ to: '/api-keys', label: hasActivePublishable ? 'Manage keys' : 'Create API key' }}
            />
            <Step
              n={3}
              done={hasFirstWallet}
              title="Create your first wallet"
              body="Use @sigilkeys/sdk in your app or call the API directly. Either way, you'll see the wallet show up in this dashboard."
              cta={
                hasFirstWallet
                  ? { to: '/wallets', label: 'View wallets' }
                  : { href: 'https://developers.sigilkeys.com/getting-started/quickstart/', label: 'Read the docs' }
              }
            />
          </ol>
        </CardBody>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: string;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-ink-tertiary">{label}</p>
      <p className={`mt-1 text-ink-primary ${mono ? 'font-mono' : ''} ${small ? 'text-xs' : 'text-sm'}`}>
        {value}
      </p>
    </div>
  );
}

type StepCta =
  | { to: string; label: string }
  | { href: string; label: string };

function Step({
  n,
  done,
  title,
  body,
  cta,
}: {
  n: number;
  done: boolean;
  title: string;
  body: string;
  cta: StepCta;
}) {
  const button = <Button variant={done ? 'secondary' : 'primary'}>{cta.label}</Button>;
  return (
    <li className="flex items-start gap-4 px-6 py-5">
      <div
        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
          done ? 'bg-ink-primary text-white' : 'border border-line text-ink-secondary'
        }`}
      >
        {done ? '✓' : n}
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-medium text-ink-primary">{title}</h3>
        <p className="mt-1 text-sm text-ink-secondary">{body}</p>
      </div>
      {'href' in cta ? (
        <a href={cta.href} target="_blank" rel="noreferrer">
          {button}
        </a>
      ) : (
        <Link to={cta.to}>{button}</Link>
      )}
    </li>
  );
}
